import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { AuthService } from '@auth0/auth0-angular';
import { Observable, of } from 'rxjs';
import { catchError, filter, switchMap, take, tap } from 'rxjs/operators';
import { AppInsightsService } from 'src/app/core/services/app-insights.service';
import { PatientService } from 'src/app/core/services/patient.service';
import { IPatientResponseDTO } from 'src/app/core/domain/patient';
import { isLoginRequiredError, isInteractiveReauthRequiredError } from 'src/app/core/auth/reauth-error';
import { initiateInteractiveReauth } from 'src/app/core/auth/interactive-reauth';
import {
  UNICORN_CAUSE_STATE_KEY,
  UNICORN_PRODUCER_STATE_KEY,
  UnicornCause,
  UnicornProducer,
} from 'src/app/core/domain/unicorn';

/**
 * Route where a recoverable resolver/token failure is surfaced. It is a
 * top-level PUBLIC route (no CustomAuthGuard / publicOnlyGuard), so navigating
 * here can never loop: an authenticated-but-token-broken user routed to `/`
 * would be bounced straight back to `/patients` by `publicOnlyGuard`, re-run
 * the resolver, and hang again. `/global-error` renders the header + a
 * "Go Back Home" action, so the app stays responsive instead of frozen.
 */
export const PATIENTS_RESOLVE_RECOVERY_ROUTE = '/global-error';

/**
 * Route used to recover from an Auth0 re-auth failure (`login_required` or
 * `missing_refresh_token`). Unlike `/global-error`, `/login` triggers a fresh
 * interactive `loginWithRedirect` (a real Auth0 authorize) which mints a new
 * token WITH a refresh token and overwrites the stale cache — self-healing the
 * account and breaking any redirect loop, because it actually re-authenticates.
 * (We must NOT route to `/` here: that loops via `publicOnlyGuard` — see above.)
 */
export const PATIENTS_RESOLVE_REAUTH_ROUTE = '/login';

/**
 * Resolver for the `/patients` (home) route.
 *
 * Two hardening behaviours defend against the native cold-start hang the Auth0
 * refresh-token migration targets, WITHOUT changing the happy path (a
 * successful resolve still emits the same `IPatientResponseDTO[]`):
 *
 * FR-011 — gate on a restored, authenticated session. We wait for the Auth0 SDK
 *   to finish loading (`isLoading$` -> false) AND report an authenticated user
 *   before firing the authenticated `GET /patients`. This closes the cold-start
 *   "tokenless window" that produced the backend 403 burst
 *   (`403 Not authenticated` = Authorization header entirely missing). The
 *   `CustomAuthGuard` already gates the parent route the same way, so on the
 *   happy path this observable resolves immediately with `isAuthenticated=true`
 *   and the getPatients call is identical to before.
 *
 * FR-012 — recover instead of hanging. Previously a resolver error aborted
 *   navigation and left the native app frozen on a blank/unicorn screen. Now a
 *   token- or request-failure is caught and the user is routed to a recoverable
 *   error state. With `useRefreshTokensFallback:false` a missing/expired refresh
 *   token fails FAST (no 60s iframe timeout), so this recovery is near-instant.
 */
export function patientsResolver(): Observable<IPatientResponseDTO[]> {
  const appInsights = inject(AppInsightsService);
  const auth = inject(AuthService);
  const router = inject(Router);
  const patientService = inject(PatientService);

  appInsights.trackTrace('patients_resolve_start');

  return auth.isLoading$.pipe(
    filter((isLoading) => !isLoading),
    take(1),
    switchMap(() => auth.isAuthenticated$.pipe(take(1))),
    switchMap((isAuthenticated) => {
      if (!isAuthenticated) {
        // FR-011: never issue an authenticated call in the tokenless window.
        // The CustomAuthGuard owns the redirect for unauthenticated users; we
        // resolve empty so navigation settles without emitting a tokenless
        // `GET /patients`.
        appInsights.trackTrace('patients_resolve_skipped_unauthenticated');
        return of([] as IPatientResponseDTO[]);
      }
      return patientService.getPatients(true).pipe(
        tap({
          error: (err) => {
            // Queue the failure breadcrumb only — the single flush() in the
            // outer catchError transmits BOTH this trace and
            // patients_resolve_recovered before navigation, so flushing here too
            // would be a redundant double-flush.
            appInsights.trackTrace('patients_resolve_fail', {
              status: (err as { status?: number })?.status,
              errorName: (err as Error)?.name ?? 'UnknownError',
              errorMessage: (err as Error)?.message,
            });
          },
        }),
      );
    }),
    // FR-012: a token/resolver failure must not abort navigation and hang the
    // app. Route to a recoverable error state and resolve empty so the router
    // completes this navigation cycle.
    catchError((err) => {
      // A stale/expired token surfaces here as an Auth0 re-auth error. The
      // recovery ACTION differs by kind (regression fix after the v1.0.13 loop):
      //
      //  - `missing_refresh_token` or `invalid_grant` ("Unknown or invalid
      //    refresh token") = broken-but-authenticated session. Force a real
      //    interactive `loginWithRedirect` (full-page /authorize) that unloads
      //    the SPA and replaces the stale cache. We MUST NOT navigate to
      //    `/login` here: that stub forwards to `/`, and `publicOnlyGuard`
      //    bounces the still-authenticated user back to `/patients`, re-running
      //    this resolver — the infinite loop #190 shipped.
      //  - `login_required` = unauthenticated. Route to `/login`
      //    (PATIENTS_RESOLVE_REAUTH_ROUTE) as #190 did.
      //  - anything else (e.g. a 500 from GET /patients) = genuine failure ->
      //    `/global-error`.
      const interactiveReauth = isInteractiveReauthRequiredError(err);
      const loginRequired = isLoginRequiredError(err);
      const recoveryRoute = loginRequired
        ? PATIENTS_RESOLVE_REAUTH_ROUTE
        : PATIENTS_RESOLVE_RECOVERY_ROUTE;
      appInsights.trackTrace('patients_resolve_recovered', {
        status: (err as { status?: number })?.status,
        errorName: (err as { error?: string; name?: string })?.error ??
          (err as Error)?.name ?? 'UnknownError',
        errorMessage: (err as Error)?.message,
        interactiveReauth,
        loginRequired,
        recoveryRoute: interactiveReauth ? 'interactive_reauth' : recoveryRoute,
      });
      appInsights.flush();
      if (interactiveReauth) {
        initiateInteractiveReauth(auth);
      } else if (recoveryRoute === PATIENTS_RESOLVE_RECOVERY_ROUTE) {
        const cause: UnicornCause = 'data-load';
        const producer: UnicornProducer = 'patients-resolver';
        void router.navigate([recoveryRoute], {
          state: {
            [UNICORN_CAUSE_STATE_KEY]: cause,
            [UNICORN_PRODUCER_STATE_KEY]: producer,
          }
        });
      } else {
        void router.navigate([recoveryRoute]);
      }
      return of([] as IPatientResponseDTO[]);
    }),
  );
}
